/**
 * Convert SVG rotate transform to CSS rotate transform
 * Handles: rotate(45) -> rotate(45deg)
 * SVG rotate with center point: rotate(45, 50, 50) -> rotate(45deg)
 */
function convertRotateTransform(match: string, value: string): string {
  const trimmed = value.trim();

  // Already has deg unit, return as is
  if (trimmed.includes('deg')) {
    return match;
  }

  const parts = trimmed.split(/\s+|,/).filter(Boolean);

  if (parts.length === 1) {
    // Simple rotation: rotate(45) -> rotate(45deg)
    return `rotate(${parts[0]}deg)`;
  } else if (parts.length === 3) {
    // SVG rotate with center point: rotate(angle, cx, cy)
    // CSS doesn't support center point in rotate, use only the angle
    return `rotate(${parts[0]}deg)`;
  }

  return match;
}

/**
 * Convert SVG translate transform to CSS translate transform
 * Handles: translate(10, 20) -> translate(10px, 20px)
 */
function convertTranslateTransform(match: string, value: string): string {
  const parts = value.split(/\s+|,/).filter(Boolean);

  if (parts.length === 1) {
    // Single value: translate(10) -> translate(10px)
    const trimmedValue = parts[0].trim();

    return `translate(${trimmedValue.includes('px') ? trimmedValue : trimmedValue + 'px'})`;
  } else if (parts.length === 2) {
    // Two values: translate(10, 20) -> translate(10px, 20px)
    const x = parts[0].trim();
    const y = parts[1].trim();

    return `translate(${x.includes('px') ? x : x + 'px'}, ${y.includes('px') ? y : y + 'px'})`;
  }

  return match;
}

/**
 * Convert SVG translateX/translateY to CSS translateX/translateY
 * Handles: translateX(10) -> translateX(10px)
 */
function convertTranslateAxisTransform(_match: string, axis: string, value: string): string {
  const trimmedValue = value.trim();

  return `translate${axis}(${trimmedValue.includes('px') ? trimmedValue : trimmedValue + 'px'})`;
}

/**
 * Convert SVG skew transform to CSS skew transform
 * Handles: skewX(45) -> skewX(45deg)
 */
function convertSkewTransform(match: string, axis: string, value: string): string {
  const trimmedValue = value.trim();

  // Already has deg unit, return as is
  if (trimmedValue.includes('deg')) {
    return match;
  }

  return `skew${axis}(${trimmedValue}deg)`;
}

/**
 * Convert SVG matrix transform to CSS matrix transform
 * Handles: matrix(a b c d e f) -> matrix(a, b, c, d, e, f)
 */
function convertMatrixTransform(match: string, value: string): string {
  // Already has commas, return as is
  if (value.includes(',')) {
    return match;
  }

  const values = value.trim().split(/\s+/).filter(Boolean);

  return `matrix(${values.join(', ')})`;
}

/**
 * Convert SVG transform syntax to CSS transform syntax
 */
export function convertSvgTransformToCSS(svgTransform: string): string {
  if (!svgTransform) return '';

  let cssTransform = svgTransform;

  // Convert rotate: rotate(45) -> rotate(45deg)
  cssTransform = cssTransform.replace(/rotate\(([^)]+)\)/g, convertRotateTransform);

  // Convert translate: translate(10, 20) -> translate(10px, 20px)
  cssTransform = cssTransform.replace(/translate\(([^)]+)\)/g, convertTranslateTransform);

  // Convert translateX/translateY
  cssTransform = cssTransform.replace(/translate([XY])\(([^)]+)\)/g, convertTranslateAxisTransform);

  // Convert skew: skewX(45) -> skewX(45deg), skewY(45) -> skewY(45deg)
  cssTransform = cssTransform.replace(/skew([XY])\(([^)]+)\)/g, convertSkewTransform);

  // Convert matrix: matrix(a b c d e f) -> matrix(a, b, c, d, e, f)
  cssTransform = cssTransform.replace(/matrix\(([^)]+)\)/g, convertMatrixTransform);

  return cssTransform;
}
